import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Markdown } from "@nodus/ui";
import { tenantApi, FAQCategory } from "@/lib/api/client";
import {
  Card,
  Badge,
  TextInput,
  Title,
  Text,
  Skeleton,
  Stack,
  Group,
  SimpleGrid,
} from "@mantine/core";
import { Search } from "lucide-react";
import { faqIcon } from "@/lib/faq-icons";
import { ArticleAttachments } from "@/components/faq/article-attachments";
import { GuidesSection } from "@/components/guides/guides-section";
import { AreaTile } from "@/components/area-tile";
import { AreaHeader } from "@/components/area-header";

type Article = FAQCategory["articles"][number];

function articleMatches(article: Article, q: string): boolean {
  const lq = q.toLowerCase();
  return article.title.toLowerCase().includes(lq) || article.content.toLowerCase().includes(lq);
}

// One article, always expanded — title, optional category badge, full body
// (markdown + attachments). No collapse/expand.
function ArticleCard({ article, categoryName }: { article: Article; categoryName?: string }) {
  return (
    <Card withBorder padding="md">
      <Group gap="xs" align="center" mb="xs">
        <Text fw={600}>{article.title}</Text>
        {categoryName && (
          <Badge variant="light" color="gray">
            {categoryName}
          </Badge>
        )}
      </Group>
      <Markdown size="sm">{article.content}</Markdown>
      <ArticleAttachments attachments={article.attachments} />
    </Card>
  );
}

export default function FAQPage() {
  const { categoryId } = useParams<{ categoryId?: string }>();
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["faq"],
    queryFn: () => tenantApi.getFAQ().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const categories = data ?? [];
  const q = search.trim();
  const hasSearch = q.length > 0;

  // Search bypasses the grid: flat list of matching articles across categories.
  const searchResults = useMemo(() => {
    if (!hasSearch) return [];
    const out: { category: FAQCategory; article: Article }[] = [];
    for (const cat of categories) {
      for (const article of cat.articles) {
        if (articleMatches(article, q)) out.push({ category: cat, article });
      }
    }
    return out;
  }, [categories, q, hasSearch]);

  // Category selection lives in the URL (/faq/:categoryId) so the browser back
  // button returns to the grid and categories are deep-linkable.
  const selected = categoryId ? categories.find((c) => String(c.id) === categoryId) ?? null : null;

  return (
    <Stack p="md" gap="md">
      <Title order={2}>Office Guide</Title>

      <TextInput
        placeholder="Search guides and FAQ..."
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<Search style={{ height: 16, width: 16 }} />}
      />

      {/* Guides (#436) sit above the FAQ on the landing view, and are searched with it. */}
      {!categoryId && <GuidesSection search={q} />}

      {!categoryId && !isLoading && !isError && categories.length > 0 && !hasSearch && (
        <Title order={3}>FAQ</Title>
      )}

      {isLoading && (
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height={132} radius="md" />
          ))}
        </SimpleGrid>
      )}

      {isError && (
        <Text fz="sm" c="red">
          Failed to load FAQ. Please try again later.
        </Text>
      )}

      {!isLoading && !isError && categories.length === 0 && <Text c="dimmed">No FAQ articles available.</Text>}

      {/* Search results — flat, across all categories, grid bypassed. */}
      {!isLoading && !isError && categories.length > 0 && hasSearch && (
        <>
          {searchResults.length === 0 ? (
            <Text c="dimmed">No articles match "{q}".</Text>
          ) : (
            <Stack gap="md">
              {searchResults.map(({ category, article }) => (
                <ArticleCard key={article.id} article={article} categoryName={category.name} />
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Category detail — deep-linked via the URL, with a back button. */}
      {!isLoading && !isError && !hasSearch && categoryId && (
        <Stack gap="md">
          <AreaHeader
            icon={faqIcon(selected?.icon ?? null)}
            title={selected?.name ?? "Category"}
            backTo="/faq"
            backLabel="All categories"
          />

          {!selected ? (
            <Text c="dimmed">Category not found.</Text>
          ) : selected.articles.length === 0 ? (
            <Text c="dimmed">No articles in this category yet.</Text>
          ) : (
            <Stack gap="md">
              {selected.articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {/* Category grid — the landing view. */}
      {!isLoading && !isError && !hasSearch && !categoryId && categories.length > 0 && (
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
          {categories.map((category) => {
            const count = category.articles.length;
            return (
              <AreaTile
                key={category.id}
                to={`/faq/${category.id}`}
                icon={faqIcon(category.icon)}
                title={category.name}
                description={`${count} article${count !== 1 ? "s" : ""}`}
              />
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}
